import type {
  GlossaryTerm,
  SearchableItem,
  SearchStrapiResponse,
} from '@app/components/Guide/types/guide.types'
import { fetchGlossaryTerms, fetchSearchResult } from '@app/services/strapiServices'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

export const guideSearchQueryKey = (query: string) => ['guide-search', query] as const

export const useGuideSearch = (query: string) => {
  const trimmed = query.trim()

  const articlesQuery = useQuery<SearchStrapiResponse>({
    queryKey: ['guide-articles-search', trimmed],
    queryFn: () => fetchSearchResult(trimmed),
    enabled: trimmed.length >= 3,
  })

  const glossaryQuery = useQuery<GlossaryTerm[]>({
    queryKey: ['glossary-terms'],
    queryFn: fetchGlossaryTerms,
  })

  const data = useMemo(() => {
    const articles: SearchableItem[] = (articlesQuery.data?.data ?? []).map((item) => ({
      ...item,
      type: 'article' as const,
    }))

    if (trimmed.length < 3) {
      return articles
    }

    const lowerQuery = trimmed.toLowerCase()
    const filteredGlossary: SearchableItem[] = (glossaryQuery.data ?? [])
      .filter(
        (term) =>
          term.term.toLowerCase().includes(lowerQuery) ||
          term.definition.toLowerCase().includes(lowerQuery),
      )
      .map((term) => ({
        documentId: term.documentId,
        term: term.term,
        definition: term.definition,
        letter: term.letter,
        slug: term.slug,
        type: 'glossary' as const,
      }))

    return [...articles, ...filteredGlossary]
  }, [articlesQuery.data, glossaryQuery.data, trimmed])

  return {
    data,
    isLoading: articlesQuery.isLoading || glossaryQuery.isLoading,
  }
}
